import { useState } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../../api/endpoints'
import { errorMessage } from '../../api/client'
import { Avatar } from '../../components/Avatar'
import { BotTag } from '../../components/BotTag'
import { Dialog } from '../../components/Dialog'
import { Icon } from '../../components/Icon'
import { useUserMap } from '../../hooks/useUsers'
import { botAsUser, findBot } from '../../lib/bots'
import { useBots } from '../../state/bots'
import { useUi } from '../../state/ui'
import { storeNewChannel } from '../channels/createConversation'

/** A bot's profile: icon, name, @handle, what it does, where its messages go, and a Message button for listed bots. */
export function BotProfileDialog() {
  const userId = useUi((s) => s.botProfile)
  const close = useUi((s) => s.closeBotProfile)
  return userId ? <BotProfile key={userId} userId={userId} onClose={close} /> : null
}

function BotProfile({ userId, onClose }: { userId: string; onClose: () => void }) {
  const navigate = useNavigate()
  const users = useUserMap()
  const { list, error } = useBots(true)
  const bot = findBot(list, userId)
  const user = users.get(userId) ?? (bot ? botAsUser(bot) : undefined)
  const name = bot?.name ?? user?.displayName ?? 'Bot'
  const handle = bot?.username || user?.username
  const description = bot?.description ?? user?.description ?? null
  const [busy, setBusy] = useState(false)
  const [messageError, setMessageError] = useState<string | null>(null)

  const message = async () => {
    setBusy(true)
    setMessageError(null)
    try {
      const summary = await api.createDm([userId])
      await storeNewChannel(summary)
      onClose()
      navigate(`/c/${summary.id}`)
    } catch (e) {
      setMessageError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={onClose} title={name} titleId="bot-profile-title" className="small-dialog bot-profile" closeLabel="Close profile">
      <div className="dialog-body">
        {/* Opens on who the bot is rather than on the X, its only button, which would look pressed. */}
        <div className="bot-profile-head" tabIndex={-1} data-autofocus>
          <Avatar user={user} id={userId} name={name} />
          <div className="bot-profile-copy">
            <p className="bot-name">
              {handle ? <span className="bot-handle">@{handle}</span> : null}
              <BotTag />
            </p>
            {description ? <p className="bot-profile-description">{description}</p> : null}
          </div>
        </div>
        {bot?.acceptsMessages ? (
          <p className="policy-notice" role="note">
            <Icon name="send" />
            <span>Messages you send are passed to {bot.webhookHost ?? 'its service'}.</span>
          </p>
        ) : bot ? (
          <p className="policy-notice" role="note">
            <Icon name="message" />
            <span>{name} posts updates and can't read replies.</span>
          </p>
        ) : null}
        {!list && !error ? <p className="notice">Loading…</p> : null}
        {messageError ? (
          <p className="field-error" role="alert">
            {messageError}
          </p>
        ) : null}
      </div>
      {/* The X closes it; the foot only holds what to do next. */}
      {bot?.listed ? (
        <div className="dialog-actions">
          <button type="button" className="button primary" disabled={busy} onClick={() => void message()}>
            <Icon name="message" />
            Message
          </button>
        </div>
      ) : null}
    </Dialog>
  )
}
